import { useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    LinearProgress,
    linearProgressClasses,
    styled,
    TextField,
    Tooltip,
} from '@mui/material';
import { AddOutlined, MonitorHeartOutlined } from '@mui/icons-material';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AdvertiserDataSetSummary } from '../../../types/DataSet';
import { Dealer } from '../../../types/Dealer';
import { useAxiosCancelToken } from '../../../hooks/AxiosCancelToken';
import AdvertiserAutocomplete from '../../../components/AdvertiserAutocomplete';
import ApiService from '../../../ApiService';
import Block from '../../../components/Block';
import CDGrid from '../../../components/CDGrid';
import Column from '../../../components/Column';
import Heading from '../../../components/Heading';
import Row from '../../../components/Row';
import Utils from '../../../components/Utils';
import usePageTitle from '../../../hooks/usePageTitle';

const QUERY_KEY = 'customAudienceSummaries';
const IDENTIFIES_THRESHOLD = 2000;
const IDENTIFIES_WARNING = 1750;

const PercentBar = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: theme.spacing(1),
    flex: 1,
    [`&.${linearProgressClasses.root}`]: {
        backgroundColor: theme.palette.grey[300],
    },
}));

const getIdentifiesColor = (count: number): 'success' | 'warning' | 'error' => {
    if (count >= IDENTIFIES_THRESHOLD) return 'success';
    if (count >= IDENTIFIES_WARNING) return 'warning';
    return 'error';
};

export default function CustomAudiencesPage() {
    usePageTitle({ segments: ['Custom Audiences'] });

    const withAxiosCancelTokenRequestConfig = useAxiosCancelToken();
    const queryClient = useQueryClient();

    const [agencyId, setAgencyId] = useState<number | null>(null);
    const [advertiserId, setAdvertiserId] = useState<number | null>(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    const { data, isLoading } = useQuery({
        queryKey: [QUERY_KEY],
        queryFn: async () => {
            const { data } = await ApiService.getCustomAudiences(null, null, withAxiosCancelTokenRequestConfig());
            return data;
        },
    });

    const createAudienceMutation = useMutation({
        mutationFn: ({ dealerId, dataSetName }: { dealerId: number; dataSetName: string }) =>
            ApiService.createAmcAudience(dealerId, dataSetName),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });

    const allRows: AdvertiserDataSetSummary[] = useMemo(() => data || [], [data]);

    const agencyOptions = useMemo(() => {
        const map = new Map<number, string>();
        allRows.forEach((row) => {
            if (row.agencyId && row.agencyName) map.set(row.agencyId, row.agencyName);
        });
        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allRows]);

    const rows: AdvertiserDataSetSummary[] = useMemo(
        () =>
            allRows.filter((row) => {
                if (agencyId != null && row.agencyId !== agencyId) return false;
                if (advertiserId != null && row.dealerId !== advertiserId) return false;
                return true;
            }),
        [allRows, agencyId, advertiserId]
    );

    const selectedAgency = agencyOptions.find((o) => o.id === agencyId) ?? null;

    const columns: GridColDef<AdvertiserDataSetSummary>[] = [
        {
            field: 'agencyName',
            headerName: 'Agency',
            width: 180,
            headerClassName: 'grid-header',
        },
        {
            field: 'dealerName',
            headerName: 'Advertiser',
            width: 280,
            headerClassName: 'grid-header',
        },
        {
            field: 'audienceName',
            headerName: 'Audience Name',
            flex: 1,
            minWidth: 160,
            headerClassName: 'grid-header',
            valueGetter: (_value, row) => row.amcAudience?.audienceName ?? '',
            renderCell: (params) => {
                const name = params.value as string;
                const dataSetName = params.row.dataSetName;
                const tooltipTitle = dataSetName
                    ? `Dataset name/type on upload: ${dataSetName}`
                    : 'Dataset name/type on upload';
                if (!name) {
                    return (
                        <Tooltip title={tooltipTitle}>
                            <span style={{ fontStyle: 'italic', color: '#888' }}>
                                {dataSetName ? `(no audience yet — ${dataSetName})` : '(no audience yet)'}
                            </span>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip title={tooltipTitle}>
                        <span>{name}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 170,
            headerClassName: 'grid-header',
            valueGetter: (_value, row) => row.amcAudience?.status ?? '',
            renderCell: (params) => {
                const status = params.row.amcAudience?.status;
                const reason = params.row.amcAudience?.statusReason;
                const dataSetName = params.row.dataSetName;
                const identified = params.row.identifiedRecords ?? 0;
                if (!status) {
                    const eligible = identified >= IDENTIFIES_THRESHOLD;
                    const placeholderLabel = eligible ? 'Ready to Create' : 'Not Created';
                    const placeholderTooltip = eligible
                        ? 'Eligible for audience creation — click + to create.'
                        : `Audience not yet created. Needs ≥ ${IDENTIFIES_THRESHOLD.toLocaleString()} identified records.`;
                    return (
                        <Tooltip title={placeholderTooltip}>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={placeholderLabel}
                                color={eligible ? 'info' : 'default'}
                            />
                        </Tooltip>
                    );
                }
                const isFailed = status.indexOf('FAILED') > -1;
                const isSucceeded = status === 'SUCCEEDED';
                // SUCCEEDED + statusReason = audience is stuck (e.g. "Invalid SQL query",
                // "Audience refresh not permitted"). Treat as Expired — the underlying
                // dataset is fine, but this audience won't refresh. Recreate to fix.
                const isExpired = isSucceeded && !!reason;
                const label = isExpired ? 'Expired' : Utils.convertCase(status);
                const color = isFailed ? 'error' : isExpired ? 'default' : isSucceeded ? 'success' : 'warning';
                const chip = <Chip size="small" variant="outlined" label={label} color={color} />;
                const tooltip = isExpired
                    ? `Expired — ${reason}. Recreate this audience to refresh with recent data.`
                    : reason
                    ? reason
                    : isSucceeded
                    ? `AMC ${dataSetName ?? ''} audience successfully created in DSP`
                    : null;
                return tooltip ? (
                    <Tooltip title={tooltip}>
                        <span>{chip}</span>
                    </Tooltip>
                ) : (
                    chip
                );
            },
        },
        {
            field: 'audienceCreation',
            headerName: 'Audience Creation',
            width: 180,
            headerClassName: 'grid-header',
            valueGetter: (_value, row) => row.amcAudience?.createTime ?? null,
            renderCell: (params) => {
                const value = params.value as number | null;
                return value ? Utils.formatDateTime(value) : '—';
            },
        },
        {
            field: 'lastUploadDate',
            headerName: 'Last Upload',
            width: 180,
            headerClassName: 'grid-header',
            valueGetter: (_value, row) => row.lastUploadDate ?? null,
            renderCell: (params) => {
                const value = params.value as number | null;
                return value ? Utils.formatDateTime(value) : '—';
            },
        },
        {
            field: 'source',
            headerName: 'Source',
            width: 110,
            headerClassName: 'grid-header',
            valueGetter: (_value, row) => {
                if (row.hasManualUpload && row.hasFtpUpload) return 'Both';
                if (row.hasFtpUpload) return 'FTP';
                if (row.hasManualUpload) return 'Manual';
                return '—';
            },
        },
        {
            field: 'identifiedRecords',
            type: 'number',
            headerName: 'Audience Health',
            width: 280,
            headerClassName: 'grid-header',
            headerAlign: 'left',
            align: 'left',
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="The identified user count (available audience) in relation to the total count of records submitted">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MonitorHeartOutlined fontSize="small" color="action" />
                        <span className="MuiDataGrid-columnHeaderTitle">Audience Health</span>
                    </Box>
                </Tooltip>
            ),
            renderCell: (params) => {
                const identified = params.row.identifiedRecords ?? 0;
                const submitted = params.row.submittedRecords ?? 0;
                if (!identified && !submitted) return '—';
                const ratio = submitted > 0 ? Math.min((identified / submitted) * 100, 100) : 0;
                const color = getIdentifiesColor(identified);
                const eligibilityTooltip =
                    identified >= IDENTIFIES_THRESHOLD
                        ? 'This advertiser has sufficient identified data for campaign use'
                        : 'Identified data is below the 2,000 minimum required for campaign use';
                const matchRatePct = submitted > 0 ? ((identified / submitted) * 100).toFixed(1) : '0.0';
                const tooltip = `${matchRatePct}% match rate — ${eligibilityTooltip}`;
                return (
                    <Tooltip title={tooltip}>
                        <Box sx={{ width: '100%' }}>
                            <Row alignItems="center" gap={1}>
                                <Box sx={{ flex: 1 }}>
                                    <PercentBar variant="determinate" value={ratio} color={color} />
                                </Box>
                                <Box
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.primary',
                                        whiteSpace: 'nowrap',
                                        minWidth: 110,
                                        textAlign: 'right',
                                    }}
                                >
                                    {Number(identified).toLocaleString()} / {Number(submitted).toLocaleString()}
                                </Box>
                            </Row>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 70,
            headerClassName: 'grid-header',
            getActions: (params: GridRowParams<AdvertiserDataSetSummary>) => {
                const identified = params.row.identifiedRecords ?? 0;
                const status = params.row.amcAudience?.status ?? '';
                const hasNoAudience = !params.row.amcAudience;
                const isFailed = status.indexOf('FAILED') > -1;
                // Show "+" for: no audience yet, OR a failed Manual audience now
                // above the threshold (data has grown enough to retry creation).
                const canCreate = hasNoAudience && identified >= IDENTIFIES_THRESHOLD;
                const canRetry = isFailed && !!params.row.hasManualUpload && identified >= IDENTIFIES_THRESHOLD;
                if (!canCreate && !canRetry) return [];
                const tooltip = canRetry ? 'Retry audience creation' : 'Create a new AMC Audience';
                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={tooltip}>
                                <AddOutlined color="primary" />
                            </Tooltip>
                        }
                        label={canRetry ? 'Retry audience' : 'Create audience'}
                        onClick={() =>
                            createAudienceMutation.mutate({
                                dealerId: params.row.dealerId,
                                dataSetName: params.row.dataSetName,
                            })
                        }
                        disabled={createAudienceMutation.isPending}
                        showInMenu={undefined}
                    />,
                ];
            },
        },
    ];

    return (
        <Column gap={0} flex={1}>
            <Block>
                <Column gap={1}>
                    <Heading>Custom Audiences</Heading>
                </Column>
            </Block>

            <Block pt={0}>
                <Row sx={{ maxWidth: 720 }}>
                    <Autocomplete
                        size="small"
                        options={agencyOptions}
                        value={selectedAgency}
                        getOptionLabel={(o) => o.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_e, newValue) => {
                            setAgencyId(newValue?.id ?? null);
                            setAdvertiserId(null);
                        }}
                        sx={{ width: '100%' }}
                        renderInput={(params) => <TextField {...params} label="Agency" variant="outlined" />}
                    />
                    <Box sx={{ width: '100%' }}>
                        <AdvertiserAutocomplete
                            size="small"
                            value={advertiserId}
                            criteria={agencyId != null ? { agencyIds: [agencyId] } : undefined}
                            onChange={(advertiser: Dealer | null) => setAdvertiserId(advertiser?.id ?? null)}
                        />
                    </Box>
                </Row>
            </Block>

            <Block flex={1}>
                <CDGrid
                    columns={columns}
                    rows={rows}
                    loading={isLoading}
                    getRowId={(row) => row.id ?? `${row.dealerId}-${row.dataSetId}`}
                    rowsPerPage={[25, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={() => setPaginationModel((m) => ({ ...m, page: 0 }))}
                    initialState={{
                        sorting: { sortModel: [{ field: 'lastUploadDate', sort: 'desc' }] },
                    }}
                />
            </Block>
        </Column>
    );
}
